import React from 'react';
import * as THREE from '../utils/three';

/**
 * Footer 背景动效：粒子波纹（透明叠加）
 * - 目标：接近 CodePen “Particle Waves WebGL Shader”的观感
 * - 约束：不改变 footer 原底色；不挡交互（pointer-events: none）
 */
export default function FooterParticleWaves({
  className = '',
  color = 0x10b981, // 绿色 (emerald-500)
  centerColor = 0x34d399, // 亮绿色 (emerald-400)
  centerIntensity = 0.9, // 中心高光强度
  opacity = 0.28, // 增加不透明度，使粒子更明显
  size = 4.4, // 增大基础粒子大小
  pointSizeFactor = 240, // 增大粒子大小因子
  density = 0.16, // 稍微增加密度，使粒子更多
  amplitude = 10,
  speed = 0.045
}) {
  const canvasRef = React.useRef(null);
  const rafRef = React.useRef(0);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const mqReduce =
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
    if (mqReduce?.matches) return;

    let renderer = null;
    let scene = null;
    let camera = null;
    let points = null;
    let geometry = null;
    let material = null;

    let width = 0;
    let height = 0;
    let t = 0;

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
    const getParent = () => canvas.parentElement || canvas;

    const computeGrid = (w, h) => {
      // 关键：点阵“铺满 footer 宽高”，避免只出现一条波带
      // density 用来控制“点的稀疏程度”（density 越小 -> step 越大 -> 点越少）
      const d = clamp(Number(density) || 0.14, 0.03, 0.9);
      const baseStep = clamp(Math.round(Math.min(w, h) / 14), 14, 28);
      const step = clamp(Math.round(baseStep / Math.sqrt(d)), 16, 60);
      const amountX = clamp(Math.round(w / step) + 1, 18, 220);
      const amountY = clamp(Math.round(h / step) + 1, 10, 140);
      return { amountX, amountY, separation: step };
    };

    const state = { amountX: 60, amountY: 30, separation: 30 };

    const initScene = () => {
      const el = getParent();
      const r = el.getBoundingClientRect();
      width = Math.max(1, Math.floor(r.width));
      height = Math.max(1, Math.floor(r.height));

      const g = computeGrid(width, height);
      state.amountX = g.amountX;
      state.amountY = g.amountY;
      state.separation = g.separation;

      scene = new THREE.Scene();
      const fov = 60;
      camera = new THREE.PerspectiveCamera(fov, width / height, 1, 8000);

      const planeW = (state.amountX - 1) * state.separation;
      const planeD = (state.amountY - 1) * state.separation;
      const vFov = THREE.MathUtils.degToRad(fov);
      const aspect = width / height;
      const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
      const distW = (planeW / 2) / Math.tan(hFov / 2);
      const distD = (planeD / 2) / Math.tan(vFov / 2);
      const dist = Math.max(distW, distD);
      camera.position.set(0, 90, dist * 1.1);

      geometry = new THREE.BufferGeometry();
      const num = state.amountX * state.amountY;
      const positions = new Float32Array(num * 3);
      const scales = new Float32Array(num);

      let i = 0;
      for (let ix = 0; ix < state.amountX; ix++) {
        for (let iy = 0; iy < state.amountY; iy++) {
          positions[i * 3] = ix * state.separation - planeW / 2;
          positions[i * 3 + 1] = 0;
          positions[i * 3 + 2] = iy * state.separation - planeD / 2;
          scales[i] = 1;
          i++;
        }
      }

      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('scale', new THREE.BufferAttribute(scales, 1));

      material = new THREE.ShaderMaterial({
        uniforms: {
          uColor: { value: new THREE.Color(color) },
          uCenterColor: { value: new THREE.Color(centerColor) },
          uCenterIntensity: { value: centerIntensity },
          uOpacity: { value: opacity },
          uSizeFactor: { value: pointSizeFactor },
          uBaseSize: { value: size }
        },
        vertexShader: `
          attribute float scale;
          uniform float uSizeFactor;
          uniform float uBaseSize;
          void main() {
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            gl_PointSize = (uBaseSize * scale) * (uSizeFactor / -mvPosition.z);
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          uniform vec3 uColor;
          uniform vec3 uCenterColor;
          uniform float uCenterIntensity;
          uniform float uOpacity;
          void main() {
            // 圆形点 + 羽化
            vec2 p = gl_PointCoord - vec2(0.5);
            float d = length(p);
            if (d > 0.5) discard;
            
            // 羽化边缘
            float a = smoothstep(0.5, 0.12, d) * uOpacity;
            
            // 中心高光：越靠中心越偏亮绿色（并略微提亮中心）
            float inner = 1.0 - clamp(d / 0.5, 0.0, 1.0);
            inner = pow(inner, 1.8); // 收紧到中心
            float k = clamp(inner * uCenterIntensity, 0.0, 1.0);
            vec3 col = mix(uColor, uCenterColor, k);
            col *= (1.0 + 0.35 * k);
            
            gl_FragColor = vec4(col, a);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending
      });

      points = new THREE.Points(geometry, material);
      points.rotation.x = -Math.PI * 0.18;
      scene.add(points);

      renderer = new THREE.WebGLRenderer({
        canvas,
        alpha: true,
        antialias: true,
        powerPreference: 'high-performance'
      });
      renderer.setClearColor(0x000000, 0);
      renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
      renderer.setSize(width, height, false);
    };

    const renderFrame = () => {
      if (!renderer || !scene || !camera || !geometry) return;

      const posAttr = geometry.getAttribute('position');
      const scaleAttr = geometry.getAttribute('scale');

      let i = 0;
      for (let ix = 0; ix < state.amountX; ix++) {
        for (let iy = 0; iy < state.amountY; iy++) {
          const y =
            Math.sin((ix + t) * 0.35) * amplitude +
            Math.sin((iy + t) * 0.50) * amplitude;
          posAttr.setY(i, y);
          scaleAttr.setX(i, (Math.sin((ix + t) * 0.25) + 1.0) * 1.2 + (Math.sin((iy + t) * 0.35) + 1.0) * 0.9 + 0.4);
          i++;
        }
      }
      posAttr.needsUpdate = true;
      scaleAttr.needsUpdate = true;

      renderer.render(scene, camera);
      t += speed;
      rafRef.current = window.requestAnimationFrame(renderFrame);
    };

    const disposeScene = () => {
      try { if (scene && points) scene.remove(points); } catch (_) {}
      try { geometry?.dispose?.(); } catch (_) {}
      try { material?.dispose?.(); } catch (_) {}
      try { renderer?.dispose?.(); } catch (_) {}
      scene = null;
      camera = null;
      points = null;
      geometry = null;
      material = null;
      renderer = null;
    };

    const onResize = () => {
      const el = getParent();
      const r = el.getBoundingClientRect();
      const w = Math.max(1, Math.floor(r.width));
      const h = Math.max(1, Math.floor(r.height));
      if (w === width && h === height) return;
      width = w;
      height = h;
      disposeScene();
      try {
        initScene();
      } catch (_) {}
    };

    try {
      initScene();
      rafRef.current = window.requestAnimationFrame(renderFrame);
      window.addEventListener('resize', onResize);
    } catch (_) {}

    return () => {
      try { window.cancelAnimationFrame(rafRef.current); } catch (_) {}
      try { window.removeEventListener('resize', onResize); } catch (_) {}
      disposeScene();
    };
  }, [amplitude, color, centerColor, centerIntensity, density, opacity, pointSizeFactor, size, speed]);

  return (
    <canvas
      ref={canvasRef}
      className={['absolute inset-0 w-full h-full pointer-events-none', className].join(' ')}
    />
  );
}

